import React, { useEffect, useState } from 'react';
import { checkAvailability, deriveNeighbourhoodFromAddress } from '../mocks/mockAvailability';
import { portalCopy } from '../copy';
import type { AvailabilityResult, PartnerBusiness } from '../types';
import { PortalButton } from './PortalUi';

type WizardStepAvailabilityProps = {
  business: PartnerBusiness;
  onContinue: () => void;
  onBack: () => void;
  onChangeAddress: () => void;
};

const WizardStepAvailability: React.FC<WizardStepAvailabilityProps> = ({
  business,
  onContinue,
  onBack,
  onChangeAddress,
}) => {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<AvailabilityResult | null>(null);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      const hood = deriveNeighbourhoodFromAddress(business.address, business.cityId);
      setResult(
        checkAvailability(business.cityId, hood.id, hood.label, business.category),
      );
      setLoading(false);
    }, 600);

    return () => clearTimeout(timer);
  }, [business.address, business.cityId, business.category]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-instrument text-3xl italic tracking-[-0.01em] text-[#2a0f2a]">
          {portalCopy.wizard.availability.title}
        </h2>
        <p className="mt-2 font-jakarta text-[15px] leading-relaxed text-[#5a3158]">
          {portalCopy.wizard.availability.subtitle}
        </p>
      </div>

      <div className="rounded-[24px] border border-[rgba(74,33,71,0.1)] bg-white p-6">
        {loading ? (
          <p className="font-jakarta text-[15px] text-[#5a3158]">{portalCopy.wizard.availability.detecting}</p>
        ) : result ? (
          <div className="space-y-5">
            <div>
              <p className="font-jakarta text-xs font-semibold uppercase tracking-wide text-[#5a3158]">
                {portalCopy.wizard.availability.neighbourhood}
              </p>
              <p className="mt-1 font-instrument text-2xl italic text-[#2a0f2a]">{result.neighbourhoodLabel}</p>
              <p className="mt-1 font-jakarta text-sm text-[#5a3158]">{business.address}</p>
            </div>

            <div className="flex items-center gap-2">
              {Array.from({ length: result.maxSlots }).map((_, index) => (
                <span
                  key={index}
                  className={`h-3 flex-1 rounded-full ${
                    index < result.occupied ? 'bg-[#5a3158]/40' : 'bg-chisme-primary'
                  }`}
                />
              ))}
            </div>
            <p className="font-jakarta text-sm text-[#5a3158]">
              {portalCopy.wizard.availability.slots(result.occupied, result.maxSlots)}
            </p>

            {result.available ? (
              <p className="rounded-2xl bg-chisme-primary/10 px-4 py-3 font-jakarta text-sm font-semibold text-chisme-primary">
                ✓ {portalCopy.wizard.availability.available}
              </p>
            ) : (
              <div className="space-y-3">
                <p className="rounded-2xl bg-red-50 px-4 py-3 font-jakarta text-sm font-semibold text-red-700">
                  {portalCopy.wizard.availability.unavailable}
                </p>
                <p className="font-jakarta text-sm text-[#5a3158]">{portalCopy.wizard.availability.unavailableHint}</p>
                <PortalButton variant="secondary" onClick={onChangeAddress}>
                  {portalCopy.wizard.availability.changeAddress}
                </PortalButton>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <PortalButton variant="secondary" fullWidth onClick={onBack}>
          {portalCopy.wizard.back_step}
        </PortalButton>
        <PortalButton fullWidth disabled={loading || !result?.available} onClick={onContinue}>
          {portalCopy.wizard.next}
        </PortalButton>
      </div>
    </div>
  );
};

export default WizardStepAvailability;
